/**
 * Agricultural Income working paper — AXIORA (Feb 2026)
 *
 * Route: /tax/agri-income
 * Categories: crop / dairy / rent-of-agri-land / nursery / other
 * The engine sums NET incomes and pushes them into the ITR computation's
 * `agriculture_income` field. Partial integration (Rule 8/9) is then applied
 * automatically inside the computation engine.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowLeft, Send, X, Check, Wheat, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { describeAxiosError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const CATEGORIES = [
  { value: "crop",             label: "Crop (wheat, rice, cotton, etc.)" },
  { value: "dairy",            label: "Dairy (own cattle on agri land)" },
  { value: "rent_agri_land",   label: "Rent of Agricultural Land" },
  { value: "nursery",          label: "Nursery — sale of saplings/seeds" },
  { value: "other",            label: "Other exempt agri receipts" },
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const currentAY = () => {
  const d = new Date();
  const fyStart = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${fyStart + 1}-${String((fyStart + 2) % 100).padStart(2, "0")}`;
};

const EMPTY_ROW = () => ({
  category: "crop",
  description: "",
  gross_receipts: 0,
  expenses: 0,
  include_in_computation: true,
  notes: "",
});

function Inner() {
  const { activeId } = useCompany();
  const nav = useNavigate();
  const [ay, setAy] = useState(currentAY());
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);      // {id?, ...row}
  const [pushOpen, setPushOpen] = useState(false);
  const [computations, setComputations] = useState([]);
  const [selectedCompId, setSelectedCompId] = useState("");

  const refresh = async () => {
    if (!activeId) return;
    setLoading(true);
    try {
      const r = await api.get(`/companies/${activeId}/agri-income/summary`, { params: { ay } });
      setRows(r.data.rows || []);
      setSummary(r.data);
    } catch (err) {
      toast.error(describeAxiosError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [activeId, ay]);

  const openNew = () => { setEditing({ ...EMPTY_ROW() }); setDialogOpen(true); };
  const openEdit = (r) => { setEditing({ ...r }); setDialogOpen(true); };

  const save = async () => {
    try {
      const payload = { ...editing, ay };
      if (editing.id) {
        await api.patch(`/companies/${activeId}/agri-income/rows/${editing.id}`, payload);
      } else {
        await api.post(`/companies/${activeId}/agri-income/rows`, payload);
      }
      toast.success("Saved");
      setDialogOpen(false);
      refresh();
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.description || CATEGORY_LABEL[r.category]}"?`)) return;
    try {
      await api.delete(`/companies/${activeId}/agri-income/rows/${r.id}`);
      toast.success("Deleted");
      refresh();
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };

  const openPushDialog = async () => {
    setPushOpen(true);
    try {
      const r = await api.get(`/companies/${activeId}/tax/computations`);
      setComputations(r.data || []);
      setSelectedCompId(r.data?.[0]?.id || "");
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };
  const pushToITR = async () => {
    if (!selectedCompId) return;
    try {
      const r = await api.post(
        `/companies/${activeId}/agri-income/push-to-itr/${selectedCompId}`,
        null, { params: { ay } },
      );
      toast.success(`Pushed net agri income ₹${formatINR(r.data.patch.agriculture_income)} → ITR`);
      setPushOpen(false);
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };

  const totals = summary?.totals || { gross: 0, expenses: 0, net: 0, included_net: 0, total_rows: 0 };
  const perCat = summary?.per_category || {};

  return (
    <>
      <PageHeader
        title="Agricultural Income · Working Paper"
        subtitle="Section 10(1) — exempt income; applied for partial integration (Rule 8/9) when applicable."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => nav("/tax")} className="h-8" data-testid="btn-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <select
              value={ay}
              onChange={(e) => setAy(e.target.value)}
              className="h-8 px-2 border border-gray-300 rounded text-xs"
              data-testid="select-ay"
            >
              {["2024-25", "2025-26", "2026-27", "2027-28"].map((a) => <option key={a} value={a}>AY {a}</option>)}
            </select>
            <Button
              onClick={openPushDialog}
              variant="secondary"
              className="h-8"
              disabled={!totals.included_net}
              data-testid="btn-push-itr"
            >
              <Send className="w-4 h-4 mr-1" /> Push to ITR
            </Button>
            <Button onClick={openNew} className="h-8 bg-emerald-700 hover:bg-emerald-800" data-testid="btn-new-row">
              <Plus className="w-4 h-4 mr-1" /> Add row
            </Button>
          </div>
        }
      />

      <div className="p-6 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="agri-kpis">
          <Kpi label="Gross Receipts" value={totals.gross} tone="gray" testid="kpi-gross" />
          <Kpi label="Expenses"       value={totals.expenses} tone="rose" testid="kpi-expenses" />
          <Kpi label="Net Agri Income" value={totals.net} tone="emerald" testid="kpi-net" />
          <Kpi label="Included in ITR" value={totals.included_net} tone="blue" testid="kpi-included" />
        </div>

        {/* Partial integration reminder */}
        <div className="border border-lime-200 bg-lime-50 rounded p-3 text-xs text-lime-900 flex gap-2" data-testid="rule-hint">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong>Partial integration (Rule 8/9)</strong> is applied automatically inside the
            ITR computation when: (a) taxpayer is Individual / HUF / AOP / BOI / AJP; AND (b) net
            agri income here &gt; ₹5,000; AND (c) non-agri slab income exceeds the basic
            exemption limit (New: ₹4L · Old: ₹2.5L / ₹3L for seniors / ₹5L for super-seniors).
          </div>
        </div>

        {/* Per-category breakdown */}
        {totals.total_rows > 0 && (
          <div className="border border-gray-200 bg-white rounded overflow-hidden" data-testid="per-category-table">
            <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 text-xs font-medium text-gray-700">Per-category breakdown</div>
            <table className="tx-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th className="text-right">Rows</th>
                  <th className="text-right">Gross</th>
                  <th className="text-right">Expenses</th>
                  <th className="text-right">Net</th>
                </tr>
              </thead>
              <tbody>
                {CATEGORIES.filter((c) => (perCat[c.value]?.rows || 0) > 0).map((c) => {
                  const cell = perCat[c.value] || { rows: 0, gross: 0, expenses: 0, net: 0 };
                  return (
                    <tr key={c.value}>
                      <td>{c.label}</td>
                      <td className="tx-num">{cell.rows}</td>
                      <td className="tx-num">{formatINR(cell.gross)}</td>
                      <td className="tx-num">{formatINR(cell.expenses)}</td>
                      <td className="tx-num font-medium">{formatINR(cell.net)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Rows */}
        <div className="border border-gray-200 bg-white rounded overflow-hidden" data-testid="rows-table">
          <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 text-xs font-medium text-gray-700 flex items-center justify-between">
            <span>Agricultural receipts — AY {ay}</span>
            {loading && <span className="text-gray-400">Loading…</span>}
          </div>
          {rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500 flex flex-col items-center gap-2">
              <Wheat className="w-10 h-10 text-gray-300" />
              No agricultural receipts recorded for AY {ay}.
            </div>
          ) : (
            <table className="tx-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Description</th>
                  <th className="text-right">Gross</th>
                  <th className="text-right">Expenses</th>
                  <th className="text-right">Net</th>
                  <th>Included?</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} data-testid={`row-${i}`}>
                    <td className="text-xs">{CATEGORY_LABEL[r.category] || r.category}</td>
                    <td className="text-xs">{r.description || "—"}</td>
                    <td className="tx-num">{formatINR(r.gross_receipts)}</td>
                    <td className="tx-num">{formatINR(r.expenses)}</td>
                    <td className="tx-num font-medium">{formatINR(r.net_income)}</td>
                    <td className="text-xs">{r.include_in_computation ? "✓" : "—"}</td>
                    <td className="whitespace-nowrap text-right">
                      <button onClick={() => openEdit(r)} className="text-blue-700 hover:underline mr-2" title="Edit" data-testid={`btn-edit-${i}`}>
                        <Pencil className="w-3.5 h-3.5 inline" />
                      </button>
                      <button onClick={() => del(r)} className="text-red-600 hover:text-red-800" title="Delete" data-testid={`btn-delete-${i}`}>
                        <Trash2 className="w-3.5 h-3.5 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="row-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit" : "Add"} Agri Income Row</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <FieldSelect label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} testid="select-category">
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </FieldSelect>
              <Field label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testid="input-description" />
              <div className="grid grid-cols-2 gap-3">
                <FieldNum label="Gross Receipts (₹)" value={editing.gross_receipts} onChange={(v) => setEditing({ ...editing, gross_receipts: v })} testid="input-gross" />
                <FieldNum label="Expenses (₹)" value={editing.expenses} onChange={(v) => setEditing({ ...editing, expenses: v })} testid="input-expenses" />
              </div>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={editing.include_in_computation}
                  onChange={(e) => setEditing({ ...editing, include_in_computation: e.target.checked })}
                  data-testid="check-include"
                />
                Include in ITR push
              </label>
              <Field label="Notes" value={editing.notes} onChange={(v) => setEditing({ ...editing, notes: v })} testid="input-notes" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="btn-cancel"><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800" data-testid="btn-save"><Check className="w-4 h-4 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Push to ITR dialog */}
      <Dialog open={pushOpen} onOpenChange={setPushOpen}>
        <DialogContent data-testid="push-dialog">
          <DialogHeader>
            <DialogTitle>Push net agri income to ITR</DialogTitle>
          </DialogHeader>
          {computations.length === 0 ? (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
              No saved computations found. <a href="/tax/computation/new" className="underline">Create one first</a> for AY {ay}.
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-xs">Choose the target computation</Label>
              <select
                value={selectedCompId}
                onChange={(e) => setSelectedCompId(e.target.value)}
                className="w-full h-9 px-2 border border-gray-300 rounded text-xs"
                data-testid="select-target-comp"
              >
                {computations.map((c) => (
                  <option key={c.id} value={c.id}>{c.ay} — {c.profile_snapshot?.name || c.profile_snapshot?.taxpayer_type}</option>
                ))}
              </select>
              <p className="text-[11px] text-gray-500">
                Pushing ₹{formatINR(totals.included_net)} of net agri income (sum of rows marked
                <em> Included</em>) into the selected computation&apos;s <code>agriculture_income</code>
                field. Partial-integration math runs on the next recompute.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPushOpen(false)}>Cancel</Button>
            <Button
              onClick={pushToITR}
              disabled={!selectedCompId}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="btn-confirm-push"
            >
              <Send className="w-4 h-4 mr-1" /> Push
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ── Small presentational helpers ─────────────────────────── */
function Kpi({ label, value, tone = "gray", testid }) {
  const bg = { gray: "bg-gray-50", rose: "bg-rose-50", emerald: "bg-emerald-50", blue: "bg-blue-50" }[tone];
  const fg = { gray: "text-gray-800", rose: "text-rose-800", emerald: "text-emerald-800", blue: "text-blue-800" }[tone];
  return (
    <div className={`border border-gray-200 ${bg} rounded p-3`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-gray-600">{label}</div>
      <div className={`text-lg font-medium ${fg}`}>₹ {formatINR(value)}</div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wider text-gray-600">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-9 text-xs" data-testid={testid} />
    </div>
  );
}
function FieldNum({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wider text-gray-600">{label}</Label>
      <Input type="number" value={value ?? 0} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} className="h-9 text-xs" data-testid={testid} />
    </div>
  );
}
function FieldSelect({ label, value, onChange, children, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wider text-gray-600">{label}</Label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-9 px-2 border border-gray-300 rounded text-xs" data-testid={testid}>
        {children}
      </select>
    </div>
  );
}

export default function AgriIncome() {
  return <RequireCompany><Inner /></RequireCompany>;
}
